import { useEffect, useMemo, useState } from "react";
import { Search, ArrowUpDown } from "lucide-react";
import { api } from "../lib/api";
import { Hero } from "../components/store/Hero";
import { Stats } from "../components/store/Stats";
import { Marquee } from "../components/store/Marquee";
import { ProductCard } from "../components/store/ProductCard";
import { ComingSoon } from "../components/store/ComingSoon";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";

const SORTS = {
  newest: { label: "Newest", fn: (a, b) => (b.created_at || "").localeCompare(a.created_at || "") },
  price_asc: { label: "Price: low to high", fn: (a, b) => a.price - b.price },
  price_desc: { label: "Price: high to low", fn: (a, b) => b.price - a.price },
  stock: { label: "Most in stock", fn: (a, b) => b.stock - a.stock },
};

export default function Store() {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    api.get("/products").then((r) => setProducts(Array.isArray(r.data) ? r.data : [])).catch(() => setProducts([]));
  }, []);

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return products.filter((p) => !s || `${p.title} ${p.subtitle} ${p.badge}`.toLowerCase().includes(s)).sort(SORTS[sort].fn);
  }, [products, q, sort]);

  return (
    <main>
      <Hero />
      <Marquee />
      <section className="mx-auto max-w-6xl px-5 py-14" id="catalog">
        <Stats products={products.length} inStock={products.filter((p) => p.stock > 0).length} />
        <div className="relative mt-10">
          <Search className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-zinc-500" />
          <input data-testid="product-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products..." className="field text-lg" style={{ paddingLeft: "3.5rem" }} />
        </div>
        <div className="mt-5 flex items-center gap-4">
          <span className="text-sm text-zinc-400" data-testid="items-count">{shown.length} items</span>
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="sort-dropdown-button" className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-zinc-300 transition-colors hover:border-violet-500/50">
              <ArrowUpDown className="h-4 w-4" /> {SORTS[sort].label}
            </DropdownMenuTrigger>
            <DropdownMenuContent className="border-white/10 bg-[#1a1633] text-zinc-200">
              {Object.entries(SORTS).map(([k, v]) => (
                <DropdownMenuItem key={k} data-testid={`sort-option-${k}`} onClick={() => setSort(k)} className="focus:bg-violet-500/20 focus:text-white">{v.label}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-7 md:grid-cols-2 lg:grid-cols-3">
          {shown.map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}
        </div>
        {shown.length === 0 && <p data-testid="no-products" className="mt-10 text-zinc-400">No products found.</p>}
      </section>
      <ComingSoon />
      <footer className="border-t border-white/[0.06] py-10 text-center text-sm text-zinc-500">© {new Date().getFullYear()} Vault Gift · Crypto payments only</footer>
    </main>
  );
}
